import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
    ArrowRight, BookOpen, Download, Truck,
    ShieldCheck, Gift, CheckCircle, AlertCircle,
    Loader2, Users, Star
} from 'lucide-react';

const BookOrder = () => {
    const navigate = useNavigate();
    const [selection, setSelection] = useState(null);

    // Form states
    const [digitalForm, setDigitalForm] = useState({ name: '', email: '', consent: false });
    const [printForm, setPrintForm] = useState({ name: '', email: '', phone: '', street: '', city: '', zip: '', note: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- HANDLERS ---
    const handleDigitalSubmit = async (e) => {
        e.preventDefault();
        if (!digitalForm.consent) {
            toast.error('Prosím potvrďte súhlas.');
            return;
        }
        setIsSubmitting(true);
        try {
            await fetch('/api/ecomail-subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: digitalForm.name, email: digitalForm.email }),
            });
            await fetch('/api/issue-download-token', { method: 'POST' });
            toast.success('Hotovo! Email s PDF je na ceste.');
            navigate('/kniha/dekujeme');
        } catch (error) {
            toast.error('Chyba pri odosielaní. Skúste to znova.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrintSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const response = await fetch('/api/ecomail-waitlist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: printForm.name,
                    email: printForm.email,
                    phone: printForm.phone,
                    address: printForm.street,
                }),
            });

            if (!response.ok) {
                const errorBody = await response.json().catch(() => ({}));
                throw new Error(errorBody?.error || 'Chyba pri rezervácii.');
            }

            toast.success('Rezervácia prijatá!');
            navigate('/kniha/dekujeme');
        } catch (error) {
            toast.error(error.message || 'Chyba pri rezervácii.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-white font-body py-8 lg:py-12 overflow-x-clip">
            <div className="container mx-auto px-4 sm:px-5 max-w-6xl min-w-0">

                {/* Back Navigation - Compact */}
                <div className="mb-8">
                    <button
                        onClick={() => selection ? setSelection(null) : navigate('/kniha')}
                        className="flex items-center text-gray-400 hover:text-brand-dark-blue transition-colors gap-2 text-sm font-medium"
                    >
                        <ArrowRight className="w-4 h-4 rotate-180" />
                        {selection ? 'Zmeniť výber verzie' : 'Späť na detail knihy'}
                    </button>
                </div>

                {!selection && (
                    <div className="animate-fade-in">

                        {/* 1. COMPACT HERO SPLIT (Top) */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16 pb-12 border-b border-gray-100">
                            {/* Left: Product Mockup */}
                            <div className="lg:col-span-5 flex flex-col items-center">
                                <div className="relative group max-w-xs transition-transform hover:scale-[1.02] duration-500">
                                    <img src="/kniha_cover.jpeg" alt="Kniha Cover" className="rounded-xl shadow-2xl" />
                                    <div className="absolute -bottom-4 -right-4 bg-brand-orange text-white p-3 rounded-lg shadow-xl flex flex-col items-center">
                                        <div className="flex text-yellow-400 group-hover:animate-pulse">
                                            {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} className="fill-current" />)}
                                        </div>
                                        <span className="text-xs font-bold mt-1 tracking-wider uppercase">4.9 / 5</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap justify-center gap-2 mt-10">
                                    <span className="px-3 py-1 bg-gray-50 rounded-lg border border-gray-100 text-[10px] text-gray-400 font-bold uppercase tracking-widest">Digital: 978-80-570-7714-5</span>
                                    <span className="px-3 py-1 bg-gray-50 rounded-lg border border-gray-100 text-[10px] text-gray-400 font-bold uppercase tracking-widest">Print: 978-80-570-7713-8</span>
                                </div>
                            </div>

                            {/* Right: Storytelling & Why */}
                            <div className="lg:col-span-7 space-y-6">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-50 border border-orange-100 text-brand-orange text-xs font-bold uppercase tracking-wide">
                                    <Gift size={12} />
                                    Verejný prísľub na Vianoce
                                </div>
                                <h1 className="text-2xl min-[400px]:text-3xl lg:text-5xl font-heading font-bold text-brand-dark-blue leading-tight break-words">
                                    Prečo je toto know-how <br />
                                    <span className="text-brand-orange underline decoration-brand-orange/30 underline-offset-8">dočasne zadarmo?</span>
                                </h1>
                                <div className="space-y-4 text-lg text-gray-600 leading-relaxed font-light">
                                    <p>
                                        Sľúbil som, že ak v našej komunite dosiahneme spoločný cieľ, dám toto know-how k dispozícii všetkým. <strong>Sľuby plním.</strong>
                                    </p>
                                    <p className="p-4 bg-gray-50 rounded-2xl border-l-4 border-brand-orange italic">
                                        "Kniha vám dá systém. Môj cieľ je vzdelať trh, aby budovanie majetku nebola lotéria."
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* 2. SELECTION GRID (Now more prominent) */}
                        <div className="text-center mb-10">
                            <h2 className="text-2xl font-bold text-brand-dark-blue uppercase tracking-widest text-sm text-gray-400 mb-2">Vyberte si svoju verziu</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10 max-w-5xl mx-auto items-stretch">

                            {/* OPTION 1: DIGITAL (Compact) */}
                            <div
                                onClick={() => setSelection('digital')}
                                className="group relative bg-white border-2 border-brand-orange/20 hover:border-brand-orange rounded-[2.5rem] p-8 shadow-xl hover:shadow-orange-500/10 transition-all cursor-pointer flex flex-col"
                            >
                                <div className="absolute -top-3 left-8 bg-brand-orange text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-lg">
                                    NAJČASTEJŠIA VOĽBA
                                </div>

                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-14 h-14 bg-brand-orange/10 rounded-2xl flex items-center justify-center text-brand-orange">
                                        <Download size={28} />
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-black text-gray-900 leading-none">0 €</div>
                                        <div className="text-[10px] text-gray-400 line-through mt-1">Bežne 14,99 €</div>
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold text-brand-dark-blue mb-2">E-kniha (PDF + Bonusy)</h3>
                                <p className="text-sm text-gray-600 mb-8 leading-relaxed">
                                    Ideálne, ak chcete začať študovať ihneď. Čítajte na mobile, tablete alebo v počítači.
                                </p>

                                <div className="mt-auto space-y-3">
                                    <div className="bg-gray-50 p-4 rounded-2xl space-y-2.5">
                                        <div className="flex items-center gap-2.5 text-xs text-gray-700">
                                            <CheckCircle size={16} className="text-green-500" />
                                            <span><strong>Okamžitý</strong> prístup do mailu</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-xs text-gray-700">
                                            <CheckCircle size={16} className="text-green-500" />
                                            <span>Pracovný zošit (PDF)</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-xs text-gray-700">
                                            <CheckCircle size={16} className="text-green-500" />
                                            <span>Bonusové video lekcie</span>
                                        </div>
                                    </div>

                                    <button className="w-full bg-brand-orange hover:bg-red-600 text-white font-bold py-4 rounded-2xl shadow-lg mt-2 transition-all duration-300">
                                        Stiahnuť PDF zdarma
                                    </button>
                                </div>
                            </div>

                            {/* OPTION 2: PRINT (Compact) */}
                            <div
                                onClick={() => setSelection('print')}
                                className="group relative bg-gray-50 border-2 border-transparent hover:border-gray-200 rounded-[2.5rem] p-8 transition-all cursor-pointer flex flex-col"
                            >
                                <div className="absolute -top-3 left-8 bg-gray-800 text-white text-[10px] font-bold px-4 py-1.5 rounded-full shadow-lg">
                                    DOČASNE VYPREDANÉ
                                </div>

                                <div className="flex justify-between items-start mb-6">
                                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-gray-600 shadow-sm">
                                        <BookOpen size={28} />
                                    </div>
                                    <div className="text-right">
                                        <div className="text-3xl font-black text-gray-900 leading-none">19,99 €</div>
                                        <div className="text-[10px] text-gray-400 mt-1">+ poštovné</div>
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold text-gray-800 mb-2">Tlačená kniha (Softcover)</h3>
                                <p className="text-sm text-gray-500 mb-8 leading-relaxed">
                                    Brožovaná väzba. Pre tých, ktorí si radi podčiarkujú. Kvôli dopytu momentálne v dotlači.
                                </p>

                                <div className="mt-auto space-y-3">
                                    <div className="bg-white p-4 rounded-2xl space-y-2.5 border border-gray-100">
                                        <div className="flex items-center gap-2.5 text-xs text-gray-600">
                                            <Users size={16} className="text-brand-orange" />
                                            <span>Zápis do poradovníka</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-xs text-gray-600">
                                            <ShieldCheck size={16} className="text-brand-orange" />
                                            <span>Platba až pri naskladnení</span>
                                        </div>
                                        <div className="flex items-center gap-2.5 text-xs text-gray-600">
                                            <Gift size={16} className="text-brand-orange" />
                                            <span>Osobný podpis autora</span>
                                        </div>
                                    </div>

                                    <button className="w-full bg-white border-2 border-brand-dark-blue text-brand-dark-blue font-bold py-4 rounded-2xl mt-2 hover:bg-brand-dark-blue hover:text-white transition-all">
                                        Predbežný záujem
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- FORMS (Untouched Logic) --- */}

                {/* 1. DIGITAL FORM */}
                {selection === 'digital' && (
                    <div className="max-w-xl mx-auto animate-fade-in-up mt-8">
                        <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 lg:p-12 border border-gray-100">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600">
                                    <Download size={24} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-brand-dark-blue">Kam máme poslať knihu?</h2>
                                    <p className="text-sm text-gray-500">Zadajte email a PDF vám príde do pár minút.</p>
                                </div>
                            </div>

                            <form onSubmit={handleDigitalSubmit} className="space-y-5">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Vaše meno</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange outline-none"
                                        placeholder="napr. Peter"
                                        value={digitalForm.name}
                                        onChange={e => setDigitalForm({ ...digitalForm, name: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-2">Váš email (pre doručenie)</label>
                                    <input
                                        type="email"
                                        required
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-orange outline-none"
                                        placeholder="email@adresa.sk"
                                        value={digitalForm.email}
                                        onChange={e => setDigitalForm({ ...digitalForm, email: e.target.value })}
                                    />
                                </div>
                                <div className="flex items-start bg-gray-50 p-4 rounded-xl">
                                    <input
                                        type="checkbox"
                                        checked={digitalForm.consent}
                                        onChange={e => setDigitalForm({ ...digitalForm, consent: e.target.checked })}
                                        className="mt-1 w-5 h-5 text-brand-orange rounded border-gray-300 focus:ring-brand-orange"
                                    />
                                    <label className="ml-3 text-sm text-gray-600">
                                        Súhlasím so zaslaním knihy a prihlásením do vzdelávacieho newslettra.
                                    </label>
                                </div>
                                <button type="submit" disabled={isSubmitting} className="w-full bg-brand-orange hover:bg-brand-dark-blue text-white font-bold py-5 rounded-2xl shadow-xl transition-all flex justify-center items-center gap-2">
                                    {isSubmitting ? <Loader2 className="animate-spin" /> : <><Download size={20} /> Stiahnuť PDF zdarma</>}
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* 2. PRINT FORM (Waitlist Context) */}
                {selection === 'print' && (
                    <div className="max-w-2xl mx-auto animate-fade-in-up mt-8">
                        <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 lg:p-12 border border-gray-100">
                            <div className="flex items-start gap-4 mb-8">
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 flex-shrink-0">
                                    <Truck size={24} />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-brand-dark-blue">Nezáväzná rezervácia</h2>
                                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">
                                        Momentálne čakáme na dotlač. Vyplnením formulára sa dostanete na <strong>prednostný zoznam</strong> (Waitlist).
                                    </p>
                                </div>
                            </div>

                            <form onSubmit={handlePrintSubmit} className="space-y-4">
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Meno a priezvisko</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-dark-blue outline-none"
                                            value={printForm.name}
                                            onChange={e => setPrintForm({ ...printForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-dark-blue outline-none"
                                                value={printForm.email}
                                                onChange={e => setPrintForm({ ...printForm, email: e.target.value })}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Telefón</label>
                                            <input
                                                type="tel"
                                                required
                                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-dark-blue outline-none"
                                                value={printForm.phone}
                                                onChange={e => setPrintForm({ ...printForm, phone: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Adresa (Ulica, Číslo, Mesto, PSČ)</label>
                                        <input
                                            type="text"
                                            required
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-dark-blue outline-none"
                                            value={printForm.street}
                                            onChange={e => setPrintForm({ ...printForm, street: e.target.value })}
                                            placeholder="Hlavná 1, 811 01 Bratislava"
                                        />
                                    </div>
                                </div>

                                <div className="bg-yellow-50 border border-yellow-100 p-4 rounded-xl flex gap-3 items-start text-xs text-yellow-800">
                                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                                    <p>
                                        <strong>Dôležité:</strong> Teraz nič neplatíte. Platobné údaje vám pošleme až po naskladnení.
                                    </p>
                                </div>

                                <button type="submit" disabled={isSubmitting} className="w-full bg-brand-dark-blue hover:bg-brand-orange text-white font-bold py-5 rounded-2xl shadow-xl transition-all flex justify-center items-center gap-2">
                                    {isSubmitting ? <Loader2 className="animate-spin" /> : <><Users size={20} /> Zapísať do poradovníka</>}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookOrder;
